"use client";
import React, { useMemo, useEffect } from "react";
import classNames from "classnames";
import { DateRangePicker } from "@/components/molecules/DatePicker";
import { IWidgetFilterItem, useWidgetFiltersController } from "./useWidgetFiltersController";
import { getQuickSelectKeyFromRange } from "@/components/molecules/DatePicker/utils";
import { PERIOD_OPTIONS, PeriodFilter } from "./PeriodFilter";
import { getWidgetComparePolicy } from "./compare-policy";

interface DateRangeSelectorProps {
    className?: string;
    label?: string;
    widget: any;
    controller: ReturnType<typeof useWidgetFiltersController>;
}

export function DateRangeSelector({ className, label, widget, controller }: DateRangeSelectorProps) {
    const {
        setSelectedPeriodItems,
        selectedDateRange,
        setSelectedDateRange,
        selectedCompareEnabled,
        selectedCompareDateRange,
        setSelectedCompareEnabled,
        setSelectedCompareDateRange,
    } = controller;

    const getMonthToDate = () => {
        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        return {
            from: firstDayOfMonth,
            to: today,
        };
    };

    const defaultValue = useMemo(() => {
        return selectedDateRange || getMonthToDate();
    }, [selectedDateRange]);

    const policy = useMemo(
        () => getWidgetComparePolicy(widget, controller.selectedPeriodItems),
        [widget, controller.selectedPeriodItems],
    );

    return (
        <div className={classNames("flex", "items-center", "gap-4")}>
            {label && (
                <p className="text-[14px] font-roboto font-medium" style={{ color: "#232F34" }}>
                    {label}
                </p>
            )}
            <DateRangePicker
                trigger={<PeriodFilter widget={widget} controller={controller} showPopoverContent={false} />}
                defaultValue={defaultValue}
                showQuickSelect
                showCompare
                showCompareOptions={policy.showCompareOptions}
                compareMode={policy.defaultCompareMode}
                compare={selectedCompareEnabled}
                compareRange={selectedCompareDateRange ?? undefined}
                className={classNames("font-roboto", "font-medium", className)}
                onChange={(value) => {
                    if (value?.from && value?.to) {
                        const quickSelectKey = getQuickSelectKeyFromRange(value, new Date());
                        if (quickSelectKey) {
                            setSelectedPeriodItems([
                                {
                                    id: quickSelectKey,
                                    text: PERIOD_OPTIONS.find((option) => option.id === quickSelectKey)?.text || "",
                                    data: quickSelectKey,
                                } as IWidgetFilterItem<string>,
                            ]);
                        } else {
                            setSelectedPeriodItems([
                                {
                                    id: "Custom",
                                    text: "Custom",
                                    data: "Custom",
                                } as IWidgetFilterItem<string>,
                            ]);
                        }
                        setSelectedDateRange?.({ from: value.from, to: value.to });
                    }
                }}
                onCompareChange={(checked) => {
                    setSelectedCompareEnabled?.(checked);
                    if (!checked) {
                        setSelectedCompareDateRange?.(null);
                    }
                }}
                onCompareRangeChange={(value) => {
                    if (value?.from && value?.to) {
                        setSelectedCompareDateRange?.({ from: value.from, to: value.to });
                    } else {
                        setSelectedCompareDateRange?.(null);
                    }
                }}
            />
        </div>
    );
}
